import { Component, computed, input, output, signal } from '@angular/core';
import type { PresenceEntry } from '@afs/chat-core';

/** The three states the online list draws (spec section 5). */
type Availability = 'on' | 'idle' | 'break';

interface StaffRow {
  entry: PresenceEntry;
  state: Availability;
}

/**
 * Who is online, and the two ways to start a chat. Pure input/output: no
 * service, no HttpClient, no Supabase.
 *
 * "Start a chat with Jane" and "Ask customer service" are the same action with
 * a different target, so they are one output with a nullable userId rather than
 * two paths that can drift apart. The parent invites the named person straight
 * after starting; a null target leaves the conversation in the pool.
 *
 * The caller is not in this list — SurveyChatService filters itself out of the
 * presence payload before it ever gets here ("leaves this person out of their
 * own staff list").
 *
 * There is no queue here and no opening-hours banner: spec section 9 gives
 * survey the online staff list and its own conversations, and the business-hours
 * gate is for `origin_app = 'portal'` only.
 *
 * Every class is prefixed `afs-chat-` and lives in this component's own inline
 * styles, so NobleUI's demo `.chat-*` set cannot reach it.
 */
@Component({
  selector: 'app-chat-staff',
  standalone: true,
  styles: [`
    /* flex:1 + min-height:0 so a long staff list scrolls inside the panel
       rather than pushing the rest of it past the panel's overflow:hidden. */
    .afs-chat-staff { flex: 1; min-height: 0; padding: .5rem .6rem; overflow-y: auto; }
    .afs-chat-staff__empty { font-size: .78rem; opacity: .72; }
    .afs-chat-staff__row {
      display: flex; align-items: center; gap: .4rem; padding: .25rem 0;
    }
    .afs-chat-staff__name { flex: 1; font-size: .84rem; }
    .afs-chat-staff__role { font-size: .72rem; opacity: .72; }
    .afs-chat-staff__row--on .afs-chat-staff__name::before { content: '● '; color: #2eb872; }
    .afs-chat-staff__row--idle .afs-chat-staff__name::before { content: '◐ '; color: #fbbc06; }
    .afs-chat-staff__row--break .afs-chat-staff__name::before { content: '○ '; opacity: .6; }
    .afs-chat-staff__message {
      width: 100%; resize: none; border: 1px solid rgba(0,0,0,.2); border-radius: 4px;
      padding: .3rem .45rem; font: inherit; font-size: .83rem; margin-top: .4rem;
    }
    .afs-chat-staff__ask { width: 100%; margin-top: .35rem; }
    /* Touch target: the field team drives this on an Android tablet. */
    @media (pointer: coarse) {
      .afs-chat-staff__message { font-size: 1rem; min-height: 44px; }
      .afs-chat-staff__start, .afs-chat-staff__ask { min-height: 40px; }
    }
  `],
  template: `
    <div class="afs-chat-staff">
      @if (rows().length === 0) {
        <p class="afs-chat-staff__empty">Nobody is online right now.</p>
      }

      @for (row of rows(); track row.entry.userId) {
        <div class="afs-chat-staff__row"
             [class.afs-chat-staff__row--on]="row.state === 'on'"
             [class.afs-chat-staff__row--idle]="row.state === 'idle'"
             [class.afs-chat-staff__row--break]="row.state === 'break'">
          <span class="afs-chat-staff__name">{{ row.entry.displayName }}</span>
          <span class="afs-chat-staff__role">{{ row.entry.role }}</span>
          <button
            class="afs-chat-staff__start btn btn-sm btn-outline-primary"
            type="button"
            [disabled]="!canStart()"
            (click)="emit(row.entry.userId)"
          >Start a chat</button>
        </div>
      }

      <textarea
        class="afs-chat-staff__message"
        rows="2"
        placeholder="What do you need?"
        aria-label="What do you need?"
        [value]="draft()"
        (input)="draft.set($any($event.target).value)"
      ></textarea>

      <button
        class="afs-chat-staff__ask btn btn-sm btn-primary"
        type="button"
        [disabled]="!canStart()"
        (click)="emit(null)"
      >Ask customer service</button>
    </div>
  `,
})
export class ChatStaffComponent {
  readonly staff = input.required<readonly PresenceEntry[]>();
  /** A start is already in flight. Set by the panel, cleared when it settles. */
  readonly busy = input<boolean>(false);

  readonly start = output<{ userId: string | null; firstMessage: string }>();

  readonly draft = signal('');

  readonly canStart = computed(() => !this.busy() && this.draft().trim().length > 0);

  readonly rows = computed<StaffRow[]>(() =>
    this.staff().map((entry) => ({
      entry,
      // A chosen break is stronger information than an idle timer, so it wins.
      //
      // break_until is read here as a moment rather than a flag, and it is the
      // one place in this app that compares one. PresenceEntry carries no
      // server-computed `meOnBreak` — that field exists only on ChatDutyDto,
      // for the caller's own break — and nothing republishes somebody else's
      // presence entry when their break merely runs out, so a non-null value
      // alone would grey them out for the rest of the day. Both sides of the
      // comparison are absolute instants, so no time zone enters into it, and
      // no countdown is drawn from it.
      state: onBreakAt(entry, Date.now()) ? 'break' : entry.idle ? 'idle' : 'on',
    })),
  );

  emit(userId: string | null): void {
    // The whole guard, not just the button's disabled attribute: the draft
    // clears the instant the first tap lands, and `[disabled]` does not reach
    // the DOM until the next round of change detection. Without this, a double
    // tap starts a second conversation whose first message is the empty string.
    if (!this.canStart()) return;
    const firstMessage = this.draft().trim();
    this.draft.set('');
    this.start.emit({ userId, firstMessage });
  }

  /**
   * The box clears optimistically the moment a Start lands, so nothing else can
   * hand the text back when the API refuses. The parent calls this; the same
   * failure and the same fix as the composer's `restore`.
   */
  restore(firstMessage: string): void {
    this.draft.set(firstMessage);
  }
}

function onBreakAt(entry: PresenceEntry, now: number): boolean {
  return entry.breakUntil !== null && Date.parse(entry.breakUntil) > now;
}
